/**
 * The open state of the rings and the shells (D161, restored in D340 round 5): opening a group does not hide the rest of the
 * picture. Every other lane compresses into a rim that keeps its names and hubs; the opened group's children fill a wide
 * ring inside it, in arcs that partition them (a phase's items by status, a department's folders by count, a folder's files
 * by kind), any level deep, with counted links from each arc to the rim. DOM-free (test/memoryRim.test.ts).
 *
 * Ported from commit 22c39eea's `armsLanes.ts` (`openRingRadius`, `partitionNodes`, `PARTITION_GAP`, `MAX_PARTITIONS`),
 * adapted to the phase-3 models: nodes are `GlobeNode`s and the levels are groups and folders, not ARMS lanes.
 */
import { type Globe, GLOBE_LAYERS, type GlobeCamera, type GlobeLayer, type GlobeNode, isFoldable, REST_BUDGET } from './memoryGlobeModel.js';

const TAU = Math.PI * 2;

/** The most arcs the opened ring is split into by kind; past that the smallest are one "Other". */
export const MAX_PARTITIONS = 8;
/** A folder per arc up to this many; more share one "Folders" arc. */
export const MAX_FOLDER_ARCS = 10;
/** The gap between arcs, in radians, and the narrowest an arc is drawn. */
export const PARTITION_GAP = 0.07;
export const MIN_ARC = 0.16;

/** What is opened: the node whose children fill the ring, and the levels above it, outermost first. */
export interface OpenFocus {
  node: number;
  chain: number[];
}

/** A group or folder that opens into a rim state: one in a tier too big to draw whole at rest. */
export function isRimLevel(globe: Globe, n: GlobeNode): boolean {
  return (n.struct === 'group' || n.struct === 'dir') && isFoldable(globe, n) && globe.counts[n.layer] > REST_BUDGET;
}

function levelsAbove(globe: Globe, n: GlobeNode): GlobeNode[] {
  const out: GlobeNode[] = [];
  let p = n.parent;
  while (p >= 0) {
    const a = globe.nodes[p];
    if (a.struct === 'group' || a.struct === 'dir') out.push(a);
    p = a.parent;
  }
  return out;
}

/** Open and on show: every level above it is open too. */
function onShow(globe: Globe, expanded: ReadonlySet<string>, n: GlobeNode): boolean {
  return isRimLevel(globe, n) && expanded.has(n.key) && levelsAbove(globe, n).every((a) => !isRimLevel(globe, a) || expanded.has(a.key));
}

/**
 * The level the ring shows the children of, or null when nothing is open (the picture at rest). The selected node's deepest
 * open level wins, so Back, Fly to and Find follow the person; otherwise the level opened last.
 */
export function openFocus(globe: Globe, expanded: ReadonlySet<string>, selectedKey: string | null): OpenFocus | null {
  let focus: GlobeNode | undefined;
  const sel = selectedKey ? globe.byKey.get(selectedKey) : undefined;
  if (sel) focus = [sel, ...levelsAbove(globe, sel)].find((n) => onShow(globe, expanded, n));
  if (!focus) {
    for (const key of expanded) {
      const n = globe.byKey.get(key);
      if (n && onShow(globe, expanded, n)) focus = n;
    }
  }
  if (!focus) return null;
  const chain = [...levelsAbove(globe, focus).filter((a) => isRimLevel(globe, a)).reverse(), focus].map((n) => n.index);
  return { node: focus.index, chain };
}

/**
 * Which nodes the rim state hides (1 for hidden): every group and layer hub stays, as the rim; the opened level's children
 * fill the ring; and the opened level itself, when it is a folder, sits at the ring's head. Everything else waits.
 */
export function rimHidden(globe: Globe, focus: OpenFocus): Uint8Array {
  const hidden = new Uint8Array(globe.nodes.length);
  for (const n of globe.nodes) {
    if (n.struct === 'layer' || n.struct === 'theme' || n.struct === 'group') continue;
    if (n.index === focus.node || n.parent === focus.node) continue;
    hidden[n.index] = 1;
  }
  return hidden;
}

/** The mask the picture is drawn under: what closed folders hide, and in the rim state what the rim hides too. */
export function maskWithRim(base: Uint8Array, rim: Uint8Array | null): Uint8Array {
  if (!rim) return base;
  const out = new Uint8Array(base.length);
  for (let i = 0; i < out.length; i++) out[i] = base[i] || rim[i] ? 1 : 0;
  return out;
}

/* ── Partitions ───────────────────────────────────────────────────────── */

/** What tells a thing apart from its siblings: a roadmap row's status, a file's kind, an area's folder, a tool's family. */
export function facetOfNode(n: Pick<GlobeNode, 'layer' | 'glyph' | 'detail' | 'label'>): string | undefined {
  if (n.layer === 'memory') {
    const file = (n.detail ?? '').split(/[\\/]/).pop() ?? '';
    const dot = file.lastIndexOf('.');
    return dot > 0 && dot < file.length - 1 ? file.slice(dot + 1).toLowerCase() : file ? 'Other' : undefined;
  }
  if (n.layer === 'architecture') {
    const segs = (n.detail ?? '').split('/').filter(Boolean);
    return segs.length > 1 ? segs[segs.length - 2] : segs[0];
  }
  if (n.layer === 'apps') {
    const at = n.label.indexOf('_');
    return at > 0 ? n.label.slice(0, at) : undefined;
  }
  return n.glyph && n.glyph !== 'group' && n.glyph !== 'community' && n.glyph !== 'note' ? n.glyph : undefined;
}

export interface RimPartition {
  /** Empty when the ring is one arc: it has no name to write. */
  label: string;
  /** Node indices, in the order they are laid out along the arc. */
  nodes: number[];
  /** What the arc stands for: files or items, or what a folder holds. Sets the arc's width. */
  count: number;
  a0: number;
  a1: number;
}

/**
 * The opened level's children as named arcs. A folder is an arc of its own, as wide as what it holds (past ten they share
 * one "Folders" arc, the biggest first); the things are arcs by what tells them apart, the biggest first (past eight the
 * smallest are "Other"); with nothing to tell them apart the things are one arc.
 */
export function partitionKids(globe: Globe, dirs: readonly number[], leaves: readonly number[], facet: (n: GlobeNode) => string | undefined = facetOfNode): RimPartition[] {
  const parts: Array<Omit<RimPartition, 'a0' | 'a1'>> = [];
  const byWeight = (a: number, b: number) => globe.nodes[b].weight - globe.nodes[a].weight || a - b;
  const sortedDirs = [...dirs].sort(byWeight);
  if (sortedDirs.length && sortedDirs.length <= MAX_FOLDER_ARCS) {
    for (const d of sortedDirs) parts.push({ label: `${globe.nodes[d].label}/`, nodes: [d], count: Math.max(1, globe.nodes[d].weight) });
  } else if (sortedDirs.length) {
    parts.push({ label: 'Folders', nodes: sortedDirs, count: sortedDirs.reduce((s, d) => s + Math.max(1, globe.nodes[d].weight), 0) });
  }
  if (leaves.length) {
    const by = new Map<string, number[]>();
    for (const l of leaves) {
      const f = facet(globe.nodes[l]) ?? 'Other';
      by.set(f, [...(by.get(f) ?? []), l]);
    }
    let groups = [...by.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
    if (groups.length > MAX_PARTITIONS) {
      const rest = groups.slice(MAX_PARTITIONS - 1).flatMap(([, v]) => v);
      groups = [...groups.slice(0, MAX_PARTITIONS - 1), ['Other', rest]];
    }
    if (groups.length < 2) {
      // One kind: the arc takes its name (every row of a phase done), unless it is only the fallback.
      const only = groups[0]?.[0];
      parts.push({ label: sortedDirs.length ? 'Files' : only && only !== 'Other' ? only : '', nodes: [...leaves].sort(byWeight), count: leaves.length });
    }
    else for (const [label, nodes] of groups) parts.push({ label, nodes: nodes.sort(byWeight), count: nodes.length });
  }
  if (!parts.length) return [];
  // Arcs as wide as their counts, none narrower than MIN_ARC.
  const single = parts.length === 1;
  const gap = single ? 0 : PARTITION_GAP;
  const avail = TAU - gap * parts.length;
  const total = parts.reduce((s, p) => s + p.count, 0) || 1;
  let spans = parts.map((p) => Math.max(single ? TAU : MIN_ARC, (p.count / total) * avail));
  const sum = spans.reduce((a, b) => a + b, 0);
  spans = spans.map((s) => (s / sum) * avail);
  let a = -Math.PI / 2 + gap / 2;
  return parts.map((p, i) => {
    const out: RimPartition = { ...p, a0: a, a1: a + spans[i] };
    a += spans[i] + gap;
    return out;
  });
}

export interface InnerPlace {
  index: number;
  angle: number;
  radius: number;
}

export interface InnerRing {
  /** The first row's radius, and the outermost row's. */
  r0: number;
  r1: number;
  places: InnerPlace[];
  /** The arcs, with the rows they ended up in. */
  arcs: Array<RimPartition & { rows: number }>;
}

/** How many rows `n` nodes need on an arc of `span` radians starting at radius `r`. */
function rowsFor(n: number, span: number, r: number, pitch: number, rowGap: number): number {
  let rows = 0;
  let room = 0;
  while (room < n && rows < 60) {
    room += Math.max(1, Math.floor((span * (r + rows * rowGap)) / pitch));
    rows++;
  }
  return Math.max(1, rows);
}

/**
 * The wide ring: each arc's nodes in rows along it, evenly spread, from `r0Min` outwards. The ring sits as near the middle as
 * holds every arc in three rows or fewer, so a big opened group is wide and a small one is tight.
 */
export function layoutInnerRing(parts: readonly RimPartition[], opts: { pitch: number; rowGap: number; r0Min: number; r0Max: number; step: number; maxRows?: number }): InnerRing {
  const maxRows = opts.maxRows ?? 3;
  let r0 = opts.r0Min;
  for (; r0 < opts.r0Max; r0 += opts.step) {
    if (parts.every((p) => rowsFor(p.nodes.length, p.a1 - p.a0 - 0.04, r0, opts.pitch, opts.rowGap) <= maxRows)) break;
  }
  const places: InnerPlace[] = [];
  let r1 = r0;
  const arcs = parts.map((p) => {
    const span = Math.max(0.02, p.a1 - p.a0 - 0.04);
    const rows = rowsFor(p.nodes.length, span, r0, opts.pitch, opts.rowGap);
    const caps = Array.from({ length: rows }, (_, j) => Math.max(1, Math.floor((span * (r0 + j * opts.rowGap)) / opts.pitch)));
    const capSum = caps.reduce((a, b) => a + b, 0);
    let at = 0;
    caps.forEach((cap, j) => {
      const left = p.nodes.length - at;
      const take = j === rows - 1 ? left : Math.min(cap, Math.max(1, Math.ceil((p.nodes.length * cap) / capSum)), left);
      const r = r0 + j * opts.rowGap;
      for (let k = 0; k < take; k++) places.push({ index: p.nodes[at + k], angle: p.a0 + 0.02 + ((k + 0.5) / take) * span, radius: r });
      at += take;
      if (take > 0) r1 = Math.max(r1, r);
    });
    return { ...p, rows };
  });
  return { r0, r1, places, arcs };
}

/* ── The Universe's shells ───────────────────────────────────────────── */

/**
 * The shells' radii with a level open: every shell compressed into a thin band at the outside (the rim), in the order they
 * had, so the opened level's children have the inside. `outer` is the radius the camera fits.
 */
export function rimRadii(globe: Globe): Record<GlobeLayer, number> {
  const present = GLOBE_LAYERS.filter((k) => globe.counts[k] > 0);
  const lo = Math.min(...present.map((k) => globe.radii[k]));
  const hi = Math.max(...present.map((k) => globe.radii[k]));
  const out = { ...globe.radii };
  const inner = 0.8 * globe.outer;
  for (const k of GLOBE_LAYERS) out[k] = hi > lo ? inner + ((globe.radii[k] - lo) / (hi - lo)) * (globe.outer - inner) : globe.outer;
  return out;
}

export interface GlobeRim {
  radii: Record<GlobeLayer, number>;
  /** Where the opened level's children (and the opened folder, at the ring's head) sit, in globe units: a flat ring facing the viewer. */
  places: Map<number, { x: number; y: number; z: number }>;
  /** The ring's arcs, for the backdrop: angle range and radius, in the ring's plane. */
  arcs: Array<{ label: string; a0: number; a1: number; r: number; count: number; layer: GlobeLayer }>;
}

/** The camera the Universe turns to when a level is open: the ring's own plane faces the viewer, a little tipped. */
export const RIM_CAMERA: GlobeCamera = { yaw: 0, pitch: 0.12, zoom: 1, panX: 0, panY: 0 };

const rimCache = new WeakMap<OpenFocus, Map<Globe, GlobeRim>>();

/** The Universe in the rim state: compressed shells, the opened level's children on a wide ring inside them in partition arcs. */
export function globeRim(globe: Globe, focus: OpenFocus): GlobeRim {
  const held = rimCache.get(focus)?.get(globe);
  if (held) return held;
  const node = globe.nodes[focus.node];
  const dirs = node.children.filter((c) => globe.nodes[c].struct === 'dir');
  const leaves = node.children.filter((c) => !globe.nodes[c].struct);
  const parts = partitionKids(globe, dirs, leaves);
  const scale = globe.outer;
  const inner = layoutInnerRing(parts, { pitch: 0.05 * scale, rowGap: 0.055 * scale, r0Min: 0.3 * scale, r0Max: 0.62 * scale, step: 0.02 * scale });
  const places = new Map<number, { x: number; y: number; z: number }>();
  // Angle 0 to the right, growing clockwise on screen: y is up.
  const at = (a: number, r: number) => ({ x: Math.cos(a) * r, y: -Math.sin(a) * r, z: 0 });
  for (const p of inner.places) places.set(p.index, at(p.angle, p.radius));
  if (node.struct === 'dir') places.set(focus.node, at(-Math.PI / 2, Math.max(0.12 * scale, inner.r0 - 0.08 * scale)));
  const out: GlobeRim = {
    radii: rimRadii(globe),
    places,
    arcs: inner.arcs.map((a) => ({ label: a.label, a0: a.a0, a1: a.a1, r: inner.r0 - 0.035 * scale, count: a.count, layer: node.layer })),
  };
  const byGlobe = rimCache.get(focus) ?? new Map<Globe, GlobeRim>();
  byGlobe.set(globe, out);
  rimCache.set(focus, byGlobe);
  return out;
}
